import { useState, useEffect } from 'react';
import axios from 'axios';
import { Warning, CheckSquare, Buildings, Calendar, ChartLine, Eye } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAT_CONFIG = [
  { key: 'reserve_actif',             label: 'Réserves',       icon: Warning,    color: '#C75B39' },
  { key: 'tache_actif',               label: 'Actions',        icon: CheckSquare, color: '#1E3A5F' },
  { key: 'incident_actif',            label: 'Incidents',      icon: Warning,    color: '#C00000' },
  { key: 'non_conformite_actif',      label: 'Non-conf.',      icon: Warning,    color: '#8B3A3A' },
  { key: 'demande_client_actif',      label: 'Demandes',       icon: Buildings,  color: '#7B5EA7' },
  { key: 'echeances_overdue',         label: 'En retard',      icon: Calendar,   color: '#C00000' },
  { key: 'a_verifier_total',          label: 'À vérifier',     icon: ChartLine,  color: '#B7791F' },  // RP16 — levées proposées
  { key: 'fait_this_month',           label: 'Levées ce mois', icon: CheckSquare, color: '#2D6A4F' },
];

function StatCard({ title, value, color, icon: Icon, testid }) {
  return (
    <div
      className="bg-white border border-[#E0E0DB] p-3 transition-all hover:shadow-sm"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#9A9A9A]">{title}</p>
        <Icon size={14} style={{ color }} />
      </div>
      <p className="text-2xl font-bold mt-1" style={{ color }}>{value ?? 0}</p>
    </div>
  );
}

export default function ElementsDashboardCard({ chantierId, onSeeAll }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/chantiers/${chantierId}/elements/badges`, { withCredentials: true })
      .then((r) => { if (!cancelled) setStats(r.data || {}); })
      .catch(() => { if (!cancelled) setStats({}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [chantierId]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 mb-4" data-testid="elements-dashboard-loading">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-[80px] bg-[#F8F7F4] border border-[#E0E0DB] animate-pulse" />
        ))}
      </div>
    );
  }

  const total = stats?.total_actif || 0;

  return (
    <div className="mb-6" data-testid="elements-dashboard-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-bold text-[#2C2C2C] uppercase tracking-wide flex items-center gap-2">
            <Eye size={16} style={{ color: '#C4773B' }} /> Vue d&apos;ensemble des observations et actions
          </h3>
          <p className="text-xs text-[#9A9A9A]">Tout ce qui est capté : terrain, visites et WhatsApp.</p>
        </div>
        {/* CHA-15 — l'ancien lien émettait un événement `switch-tab` que personne
            n'écoutait : le parent fournit désormais la navigation (onglet Tâches). */}
        {onSeeAll && (
          <button
            type="button"
            onClick={onSeeAll}
            className="text-xs text-[#1E3A5F] hover:underline font-semibold"
            data-testid="elements-dashboard-link"
          >
            Voir tout →
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        {STAT_CONFIG.map((s) => (
          <StatCard
            key={s.key}
            title={s.label}
            value={stats?.[s.key] || 0}
            color={s.color}
            icon={s.icon}
            testid={`elements-dashboard-${s.key}`}
          />
        ))}
      </div>

      {total === 0 && (
        <p className="text-xs text-[#9A9A9A] italic mt-3" data-testid="elements-dashboard-empty">
          Aucun élément actif. Capturez sur le terrain pour alimenter ce tableau de bord.
        </p>
      )}
    </div>
  );
}
